import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import type { ScreenerAsset } from "./types";
import { assetHasCorpus } from "~/modules/asset_chat/assets";
import type { MultiSelectOption } from "~/components/MultiSelect";
import { SortOptions } from "./AssetScreenerSortInput";
import { sortItemsByProperty } from "~/utils/functions/sortItemsByProperty";
import { SortDirections } from "~/modules/filters/types";
import { IconTypes } from "~/components/Icon/types";
import { getAssetClassLinkMetadata } from "~/utils/getAssetClassLinkMetadata";

// // // //

export const DATA_ROOM_ENABLED_PARAMTER = "data_room";
export const DATA_ROOM_ENABLED_PARAMETER_VALUE = "true";
export const SEARCH_QUERY_PARAMETER = "search";

const REDEMPTION_TERMS = [
    "Daily",
    "Weekly",
    "Monthly",
    "Quarterly",
    "Maturity only",
] as const;

function compareNullableDesc(
    a: number | null | undefined,
    b: number | null | undefined,
): number {
    if (a == null && b == null) {
        return 0;
    }

    if (a == null) {
        return 1;
    }

    if (b == null) {
        return -1;
    }

    return b - a;
}

function getYieldValues(assets: ScreenerAsset[]): number[] {
    return assets.map((asset) => asset.stats.return ?? 0).filter((y) => y > 0);
}

function getMaxYield(yields: number[]): number {
    if (yields.length === 0) {
        return 1;
    }

    return Math.ceil(Math.max(...yields) + 2);
}

/**
 * Get asset class options with counts from the assets list.
 */
function getAssetClassOptions(assets: ScreenerAsset[]): MultiSelectOption[] {
    // Count assets per asset class
    const counts: Record<string, number> = {};
    const icons: Record<string, IconTypes | null> = {};

    assets.forEach((asset) => {
        const assetClassMetadata = getAssetClassLinkMetadata(
            asset.asset_class.id,
        );
        const key = asset.asset_class.id.toString();
        counts[key] = (counts[key] || 0) + 1;
        if (!icons[key]) {
            icons[key] = assetClassMetadata.icon || null;
        }
    });

    // Get asset class metadata

    // Get unique asset classes
    const uniqueClasses = new Map<string, (typeof assets)[0]["asset_class"]>();
    assets.forEach((asset) => {
        const key = asset.asset_class.id.toString();
        if (!uniqueClasses.has(key)) {
            uniqueClasses.set(key, asset.asset_class);
        }
    });

    const options = Array.from(uniqueClasses.values()).map((cls) => ({
        value: cls.id.toString(),
        label: cls.name,
        count: counts[cls.id.toString()] || 0,
        icon: icons[cls.id.toString()] || undefined,
    }));

    return sortItemsByProperty({
        items: options,
        field: "count",
        direction: SortDirections.desc,
    });
}

/**
 * Get redemption options with counts from the assets list.
 */
function getRedemptionOptions(assets: ScreenerAsset[]): MultiSelectOption[] {
    const counts: Record<string, number> = {};

    assets.forEach((asset) => {
        const label = asset.redemptionFrequency.label;

        if (!label) {
            return;
        }

        counts[label] = (counts[label] || 0) + 1;
    });

    return REDEMPTION_TERMS.map((term) => ({
        label: term,
        value: term,
        count: counts[term] || 0,
    }));
}

/**
 * Get network options with counts from the assets list. Networks have no
 * stable id on ScreenerAsset, so the name doubles as the option value.
 */
function getNetworkOptions(assets: ScreenerAsset[]): MultiSelectOption[] {
    const counts: Record<string, number> = {};
    const icons: Record<string, string | null> = {};

    assets.forEach((asset) => {
        asset.networks.forEach((network) => {
            counts[network.name] = (counts[network.name] || 0) + 1;
            if (!icons[network.name]) {
                icons[network.name] = network.icon;
            }
        });
    });

    const options = Object.keys(counts).map((name) => ({
        value: name,
        label: name,
        count: counts[name],
        icon: icons[name] || undefined,
    }));

    return sortItemsByProperty({
        items: options,
        field: "count",
        direction: SortDirections.desc,
    });
}

/**
 * Custom hook for managing investment asset filtering and sorting state.
 */
export function useAssetFilters(assets: ScreenerAsset[]) {
    const assetClassOptions = useMemo(
        () => getAssetClassOptions(assets),
        [assets],
    );

    const redemptionOptions = useMemo(
        () => getRedemptionOptions(assets),
        [assets],
    );

    const networkOptions = useMemo(() => getNetworkOptions(assets), [assets]);

    const globalMaxYield = useMemo(
        () => getMaxYield(getYieldValues(assets)),
        [assets],
    );

    const [searchQuery, setSearchQuery] = useState("");

    const [selectedAssetClasses, setSelectedAssetClasses] = useState<string[]>(
        [],
    );

    const [yieldRange, setYieldRange] = useState<[number, number]>([
        0,
        globalMaxYield,
    ]);
    const [selectedRedemption, setSelectedRedemption] = useState<string[]>([]);
    const [selectedNetworks, setSelectedNetworks] = useState<string[]>([]);
    const [nonKycOnly, setNonKycOnly] = useState(false);

    const [sortBy, setSortBy] = useState<SortOptions>(SortOptions.dataRoom);

    // Id of the asset whose row should start expanded on first load (seeded from
    // the deep link below). `null` once there is nothing to auto-expand.
    const [initialExpandedAssetId, setInitialExpandedAssetId] = useState<
        number | null
    >(null);

    // Deep-link support (e.g. the homepage Active Opportunities card links to
    // /asset-screener?data_room=true&search=ACRED). The page is statically generated,
    // so query params are only readable after hydration — apply them once when
    // the router is ready. The data_room param no longer toggles a filter (the
    // checkbox was removed); it pins the Data Room sort and marks that the link
    // may point at a single deal to expand.
    const router = useRouter();
    const hasAppliedQueryParams = useRef(false);
    useEffect(() => {
        if (!router.isReady || hasAppliedQueryParams.current) {
            return;
        }
        hasAppliedQueryParams.current = true;

        const dataRoomEnabled =
            router.query[DATA_ROOM_ENABLED_PARAMTER] ===
            DATA_ROOM_ENABLED_PARAMETER_VALUE;
        if (dataRoomEnabled) {
            setSortBy(SortOptions.dataRoom);
        }

        const searchParam = router.query[SEARCH_QUERY_PARAMETER];
        const searchValue =
            typeof searchParam === "string" && searchParam.length > 0
                ? searchParam
                : null;

        // When the deep link carries both the data-room filter and a search
        // term it points at a single deal, so expand that asset's row on first
        // load and drop the visitor straight into its details/data room.
        if (dataRoomEnabled && searchValue) {
            const query = searchValue.toLowerCase();
            const matchedDeepLinkAsset = assets.find(
                (asset) =>
                    assetHasCorpus(asset) &&
                    (asset.symbol.toLowerCase() === query ||
                        asset.symbol.toLowerCase().includes(query) ||
                        asset.name.toLowerCase().includes(query)),
            );
            if (matchedDeepLinkAsset) {
                setInitialExpandedAssetId(matchedDeepLinkAsset.id);
            }
        }

        if (searchValue) {
            setSearchQuery(searchValue);
        }

        // The params were only needed to seed this initial load; strip them so a
        // refresh or shared URL doesn't re-trigger the auto-expand and the URL
        // stays clean as the visitor changes filters.
        if (dataRoomEnabled || searchValue) {
            const {
                [DATA_ROOM_ENABLED_PARAMTER]: _dataRoom,
                [SEARCH_QUERY_PARAMETER]: _search,
                ...rest
            } = router.query;
            void _dataRoom;
            void _search;
            void router.replace(
                { pathname: router.pathname, query: rest },
                undefined,
                { shallow: true },
            );
        }
    }, [router, assets]);

    const yieldCandidateAssets = useMemo(() => {
        return assets.filter((asset) => {
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                if (
                    !asset.name.toLowerCase().includes(query) &&
                    !asset.symbol.toLowerCase().includes(query) &&
                    !asset.platform?.name.toLowerCase().includes(query)
                ) {
                    return false;
                }
            }

            if (
                selectedAssetClasses.length > 0 &&
                !selectedAssetClasses.includes(asset.asset_class.id.toString())
            ) {
                return false;
            }

            if (
                selectedRedemption.length > 0 &&
                !selectedRedemption.includes(
                    asset.redemptionFrequency.label || "",
                )
            ) {
                return false;
            }

            if (
                selectedNetworks.length > 0 &&
                !asset.networks.some((network) =>
                    selectedNetworks.includes(network.name),
                )
            ) {
                return false;
            }

            if (nonKycOnly && !asset.isNonKyc) {
                return false;
            }

            return true;
        });
    }, [
        assets,
        searchQuery,
        selectedAssetClasses,
        selectedRedemption,
        selectedNetworks,
        nonKycOnly,
    ]);

    const allYields = useMemo(
        () => getYieldValues(yieldCandidateAssets),
        [yieldCandidateAssets],
    );

    const filteredAssets = useMemo(() => {
        const result = yieldCandidateAssets.filter((asset) => {
            const assetYield = asset.stats.return ?? 0;
            if (assetYield < yieldRange[0] || assetYield > yieldRange[1]) {
                return false;
            }

            return true;
        });

        result.sort((a, b) => {
            switch (sortBy) {
                // Data-room assets float to the top of the list under this
                // sort; within that group and below it rows fall back to AUM
                // descending. The screener page additionally splits the two
                // groups into their own sections, which preserves this order.
                case "data-room": {
                    const aHasDataRoom = assetHasCorpus(a);
                    const bHasDataRoom = assetHasCorpus(b);
                    if (aHasDataRoom !== bHasDataRoom) {
                        return aHasDataRoom ? -1 : 1;
                    }
                    return (b.stats.aum ?? 0) - (a.stats.aum ?? 0);
                }
                case "aum-desc":
                    return (b.stats.aum ?? 0) - (a.stats.aum ?? 0);
                case "yield-desc":
                    return (b.stats.return ?? 0) - (a.stats.return ?? 0);
                case "yield-asc":
                    return (a.stats.return ?? 0) - (b.stats.return ?? 0);
                case "return-1y-desc":
                    return compareNullableDesc(
                        a.returns.trailing1Year,
                        b.returns.trailing1Year,
                    );
                case "return-1m-desc":
                    return compareNullableDesc(
                        a.returns.trailing1Month,
                        b.returns.trailing1Month,
                    );
                case "aum-asc":
                    return (a.stats.aum ?? 0) - (b.stats.aum ?? 0);
                default:
                    return 0;
            }
        });

        return result;
    }, [yieldCandidateAssets, yieldRange, sortBy]);

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (searchQuery) count++;
        if (selectedAssetClasses.length > 0) count++;
        if (yieldRange[0] !== 0 || yieldRange[1] !== globalMaxYield) count++;
        if (selectedRedemption.length > 0) count++;
        if (selectedNetworks.length > 0) count++;
        if (nonKycOnly) count++;
        return count;
    }, [
        searchQuery,
        selectedAssetClasses,
        yieldRange,
        selectedRedemption,
        selectedNetworks,
        nonKycOnly,
        globalMaxYield,
    ]);

    const clearFilters = () => {
        setSearchQuery("");
        setSelectedAssetClasses([]);
        setYieldRange([0, globalMaxYield]);
        setSelectedRedemption([]);
        setSelectedNetworks([]);
        setNonKycOnly(false);
    };

    return {
        // State
        searchQuery,
        setSearchQuery,
        selectedAssetClasses,
        setSelectedAssetClasses,
        yieldRange,
        setYieldRange,
        selectedRedemption,
        setSelectedRedemption,
        selectedNetworks,
        setSelectedNetworks,
        nonKycOnly,
        setNonKycOnly,
        sortBy,
        setSortBy,
        // Computed values
        allYields,
        maxYield: globalMaxYield,
        assetClassOptions,
        redemptionOptions,
        networkOptions,
        filteredAssets,
        activeFilterCount,
        initialExpandedAssetId,
        // Functions
        clearFilters,
    };
}
